/**
 * Organization → Information: read-only org fields and editable contacts.
 */
import { useEffect, useMemo, useState } from "react";
import adminStyles from "../ui/AdminStyles";
import { COUNTRIES } from "../ui/Countries";
import { authedGet, authedPut } from "../api/customerAuthedFetch";
import {
  CONTACT_EMAIL_FIELDS,
  contactEmailsPayload,
  withNormalizedContactEmails,
} from "../ui/contactEmails";
import ContactEmailsEditor from "../ui/ContactEmailsEditor";
import InfoTip from "../components/InfoTip";

/** Contacts editor kept in code; hide until contact info has a clearer product use. */
const SHOW_CONTACTS_SECTION = false;

const TIPS = {
  customerId: "Stable identifier for your organization in AIxMed Cloud.",
  displayName: "Short name shown in the portal header and lists.",
  officialName: "Legal / official organization name on record.",
  country: "Country of registration for the organization.",
  timezone:
    "Organization headquarters timezone. Used as a default for time displays where applicable.",
  contacts:
    "Role contact email lists for your organization. Any signed-in portal user can edit and save.",
  saveContacts: "Save contact email changes to your organization profile.",
  resetContacts: "Discard unsaved contact edits and restore the last saved lists.",
};

const SECTION_CARD = {
  marginTop: 28,
  padding: "20px 22px 22px",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  background: "linear-gradient(180deg, #ffffff 0%, #fafbfc 100%)",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.05), 0 1px 3px rgba(15, 23, 42, 0.04)",
};

function countryName(code) {
  const c = COUNTRIES.find((x) => x.code === code);
  return c ? c.name : code || "—";
}

function contactListsEqual(a, b) {
  for (const field of CONTACT_EMAIL_FIELDS) {
    const left = JSON.stringify(a?.[field] || []);
    const right = JSON.stringify(b?.[field] || []);
    if (left !== right) return false;
  }
  return true;
}

export default function CustomerOrgDetails() {
  const [profile, setProfile] = useState(null);
  const [contactsBaseline, setContactsBaseline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [contactsSaving, setContactsSaving] = useState(false);
  const [contactsSaveError, setContactsSaveError] = useState("");
  const [contactsSaveOk, setContactsSaveOk] = useState(false);

  useEffect(() => {
    document.title = "Organization · Information";
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr("");
    authedGet("/customer/profile")
      .then((data) => {
        if (cancelled) return;
        const normalized = withNormalizedContactEmails(data);
        setProfile(normalized);
        setContactsBaseline(contactEmailsPayload(normalized));
      })
      .catch((e) => {
        if (!cancelled) setErr(String(e.message || e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const contactsDirty = useMemo(() => {
    if (!profile || !contactsBaseline) return false;
    return !contactListsEqual(contactEmailsPayload(profile), contactsBaseline);
  }, [profile, contactsBaseline]);

  async function handleSaveContacts() {
    if (!profile || !contactsDirty) return;
    setContactsSaving(true);
    setContactsSaveError("");
    setContactsSaveOk(false);
    try {
      const payload = contactEmailsPayload(profile);
      const data = await authedPut("/customer/profile", payload);
      const next = withNormalizedContactEmails({ ...profile, ...data });
      setProfile(next);
      setContactsBaseline(contactEmailsPayload(next));
      setContactsSaveOk(true);
    } catch (e) {
      setContactsSaveError(String(e.message || e));
    } finally {
      setContactsSaving(false);
    }
  }

  function handleResetContacts() {
    if (!contactsBaseline) return;
    setProfile((prev) => ({ ...prev, ...contactsBaseline }));
    setContactsSaveError("");
    setContactsSaveOk(false);
  }

  if (loading) return <p>Loading…</p>;
  if (err) return <p style={{ color: "red" }}>{err}</p>;
  if (!profile) return null;

  const status = (profile.customer_status || "active").toLowerCase();

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          paddingBottom: 18,
          marginBottom: 4,
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <h1 style={{ margin: 0 }}>{profile.display_name}</h1>
            <div
              style={{
                padding: "4px 10px",
                borderRadius: 12,
                fontSize: 13,
                fontWeight: 600,
                background: status === "active" ? "#dcfce7" : "#fee2e2",
                color: status === "active" ? "#166534" : "#991b1b",
              }}
            >
              {status}
            </div>
          </div>
          <div style={{ color: "#6b7280", fontSize: 14 }}>
            {profile.official_name}
          </div>
        </div>
      </div>

      <section style={{ ...SECTION_CARD, marginTop: 20 }}>
        <h3 style={{ marginTop: 0, marginBottom: 16 }}>General Information</h3>
        <table style={adminStyles.table}>
          <tbody>
            <Row tip={TIPS.customerId} label="Customer ID">
              <code>{profile.customer_id}</code>
            </Row>
            <Row tip={TIPS.displayName} label="Display Name">
              {profile.display_name || "—"}
            </Row>
            <Row tip={TIPS.officialName} label="Official Name">
              {profile.official_name || "—"}
            </Row>
            <Row tip={TIPS.country} label="Country">
              {countryName(profile.country_of_registration)}
            </Row>
            <Row tip={TIPS.timezone} label="HQ Timezone">
              {profile.hq_timezone || "UTC"}
            </Row>
          </tbody>
        </table>
      </section>

      {SHOW_CONTACTS_SECTION ? (
        <section style={{ ...SECTION_CARD, marginBottom: 32 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 12,
              marginBottom: 12,
            }}
          >
            <div>
              <h3
                style={{
                  marginTop: 0,
                  marginBottom: 6,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                Contacts
                <InfoTip text={TIPS.contacts} />
              </h3>
              <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>
                Role contact emails for your organization. Any portal user can edit.
              </p>
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "center" }}>
              <button
                type="button"
                disabled={!contactsDirty || contactsSaving}
                onClick={() => void handleSaveContacts()}
              >
                {contactsSaving ? "Saving…" : "Save contacts"}
              </button>
              <InfoTip text={TIPS.saveContacts} align="end" />
              <button
                type="button"
                disabled={!contactsDirty || contactsSaving}
                onClick={handleResetContacts}
              >
                Reset
              </button>
              <InfoTip text={TIPS.resetContacts} align="end" />
            </div>
          </div>
          {contactsSaveError && (
            <p style={{ color: "#991b1b", marginTop: 0 }}>{contactsSaveError}</p>
          )}
          {contactsSaveOk && !contactsDirty && (
            <p style={{ color: "#166534", marginTop: 0 }}>Contacts saved.</p>
          )}
          {contactsDirty && (
            <p style={{ color: "#b45309", fontSize: 13, marginTop: 0 }}>
              Unsaved contact changes
            </p>
          )}
          <ContactEmailsEditor profile={profile} setProfile={setProfile} />
        </section>
      ) : null}
    </div>
  );
}

function Row({ label, tip, children }) {
  return (
    <tr>
      <td style={{ ...adminStyles.td, fontWeight: 600, width: 220 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          {label}
          {tip ? <InfoTip text={tip} /> : null}
        </span>
      </td>
      <td style={adminStyles.td}>{children}</td>
    </tr>
  );
}
